/**
 * Proactive Assistant
 * Suggests actions before user asks based on patterns, context, and ML
 * Learns user routines and provides timely recommendations
 */

import { contextManager, AppContext, ContextualSuggestion } from './ContextManager';
import { tenantLearningEngine } from './TenantLearningEngine';
import { getCompanyId } from '../tenantContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/client';

export interface ProactiveSuggestion extends ContextualSuggestion {
  type: 'time-based' | 'pattern' | 'overdue' | 'incomplete' | 'predictive';
  confidence: number;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  actionable: boolean;
  dismissible: boolean;
  expiresAt?: Date;
}

export interface UserPattern {
  id: string;
  companyId: string;
  userId: string;
  type: 'daily' | 'weekly' | 'monthly';
  timeOfDay?: number; // Hour (0-23)
  dayOfWeek?: number; // 0-6 (Sunday-Saturday)
  dayOfMonth?: number; // 1-31
  action: string;
  frequency: number; // How many times observed
  lastOccurrence: Date;
  confidence: number;
}

class ProactiveAssistant {
  private static instance: ProactiveAssistant;
  private patterns: Map<string, UserPattern[]> = new Map(); // userId -> patterns
  private dismissedSuggestions: Set<string> = new Set();

  private constructor() {
    this.loadPatterns();
  }

  static getInstance(): ProactiveAssistant {
    if (!ProactiveAssistant.instance) {
      ProactiveAssistant.instance = new ProactiveAssistant();
    }
    return ProactiveAssistant.instance;
  }

  /**
   * Get proactive suggestions for current context
   */
  async getSuggestions(
    context: AppContext,
    includeTypes?: Array<ProactiveSuggestion['type']>
  ): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];
    const now = new Date();
    const companyId = context.companyId || getCompanyId();

    // 1. Time-based suggestions (morning routine, end of day, etc.)
    const timeBased = await this.getTimeBasedSuggestions(context, now);
    suggestions.push(...timeBased);

    // 2. Pattern-based suggestions (learned user behavior)
    const patternBased = await this.getPatternBasedSuggestions(context, now);
    suggestions.push(...patternBased);

    // 3. Overdue items
    const overdue = await this.getOverdueSuggestions(companyId);
    suggestions.push(...overdue);

    // 4. Incomplete work
    const incomplete = await this.getIncompleteSuggestions(companyId, context.userId);
    suggestions.push(...incomplete);

    // 5. Predictive suggestions (ML-based)
    const predictive = await this.getPredictiveSuggestions(context, companyId);
    suggestions.push(...predictive);

    // Filter by type if specified
    let filtered = includeTypes && includeTypes.length > 0
      ? suggestions.filter(s => includeTypes.includes(s.type))
      : suggestions;

    // Remove dismissed suggestions
    filtered = filtered.filter(s => !this.dismissedSuggestions.has(this.getSuggestionKey(s)));

    // Remove expired suggestions
    filtered = filtered.filter(s => !s.expiresAt || s.expiresAt > now);

    // Sort by urgency and confidence
    filtered.sort((a, b) => {
      const urgencyOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const urgencyDiff = urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
      if (urgencyDiff !== 0) return urgencyDiff;
      return b.confidence - a.confidence;
    });

    // Limit to top 10
    return filtered.slice(0, 10);
  }

  /**
   * Time-based suggestions (morning routine, etc.)
   */
  private async getTimeBasedSuggestions(
    context: AppContext,
    now: Date
  ): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];
    const hour = now.getHours();
    const screen = context.currentScreen;

    // Morning shift start (6-9 AM)
    if (hour >= 6 && hour < 9) {
      suggestions.push({
        type: 'time-based',
        text: '☀️ Start your daily route',
        command: 'show my route',
        action: 'show_route',
        priority: 10,
        reason: 'Morning shift - time to check your wells',
        confidence: 0.9,
        urgency: 'high',
        actionable: true,
        dismissible: true,
        icon: 'sunrise',
        expiresAt: new Date(now.getTime() + 3 * 60 * 60 * 1000) // Expires in 3 hours
      });

      if (screen === 'dashboard') {
        suggestions.push({
          type: 'time-based',
          text: '📋 Check overnight alerts',
          command: 'show alerts from last 12 hours',
          action: 'show_recent_alerts',
          priority: 9,
          reason: 'Review what happened overnight',
          confidence: 0.85,
          urgency: 'medium',
          actionable: true,
          dismissible: true,
          icon: 'bell'
        });
      }
    }

    // Mid-day (11 AM - 2 PM)
    if (hour >= 11 && hour < 14) {
      suggestions.push({
        type: 'time-based',
        text: '🍔 Log lunch break',
        command: 'start lunch break',
        action: 'clock_break',
        priority: 5,
        reason: 'Track your break time',
        confidence: 0.7,
        urgency: 'low',
        actionable: true,
        dismissible: true,
        icon: 'clock'
      });
    }

    // End of shift (3-6 PM)
    if (hour >= 15 && hour < 18) {
      suggestions.push({
        type: 'time-based',
        text: '📝 Complete daily summary',
        command: 'show incomplete entries',
        action: 'show_drafts',
        priority: 8,
        reason: 'Finish pending work before end of day',
        confidence: 0.88,
        urgency: 'high',
        actionable: true,
        dismissible: false,
        icon: 'clipboard-check'
      });

      suggestions.push({
        type: 'time-based',
        text: '⏰ Clock out',
        command: 'clock out',
        action: 'clock_out',
        priority: 7,
        reason: 'End your shift',
        confidence: 0.8,
        urgency: 'medium',
        actionable: true,
        dismissible: true,
        icon: 'log-out'
      });
    }

    return suggestions;
  }

  /**
   * Pattern-based suggestions from learned behavior
   */
  private async getPatternBasedSuggestions(
    context: AppContext,
    now: Date
  ): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];
    const userPatterns = this.patterns.get(context.userId) || [];

    for (const pattern of userPatterns) {
      // Check if pattern matches current time
      if (this.patternMatchesTime(pattern, now)) {
        suggestions.push({
          type: 'pattern',
          text: `Based on your routine: ${pattern.action}`,
          command: pattern.action,
          action: pattern.action,
          priority: 7,
          reason: `You usually do this ${pattern.type} at this time`,
          confidence: pattern.confidence,
          urgency: 'medium',
          actionable: true,
          dismissible: true,
          icon: 'repeat'
        });
      }
    }

    return suggestions;
  }

  /**
   * Overdue wells and tasks
   */
  private async getOverdueSuggestions(companyId: string): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];

    try {
      // Check overdue wells
      const wellsRef = collection(db, `companies/${companyId}/wells`);
      const overdueQuery = query(
        wellsRef,
        where('status', '==', 'active'),
        where('nextCheckDate', '<', new Date()),
        limit(10)
      );
      
      const wellsSnapshot = await getDocs(overdueQuery);
      
      if (wellsSnapshot.size > 0) {
        suggestions.push({
          type: 'overdue',
          text: `⚠️ ${wellsSnapshot.size} wells overdue for check`,
          command: 'show overdue wells',
          action: 'filter_wells_overdue',
          priority: 10,
          reason: 'Wells need immediate attention',
          confidence: 1.0,
          urgency: 'critical',
          actionable: true,
          dismissible: false,
          icon: 'alert-triangle'
        });
      }

      // Check overdue tickets
      const ticketsRef = collection(db, `companies/${companyId}/tickets`);
      const overdueTicketsQuery = query(
        ticketsRef,
        where('status', 'in', ['open', 'assigned', 'in_progress']),
        where('dueDate', '<', new Date()),
        limit(10)
      );

      const ticketsSnapshot = await getDocs(overdueTicketsQuery);

      if (ticketsSnapshot.size > 0) {
        suggestions.push({
          type: 'overdue',
          text: `🔧 ${ticketsSnapshot.size} tickets overdue`,
          command: 'show overdue tickets',
          action: 'filter_tickets_overdue',
          priority: 9,
          reason: 'Service tickets need resolution',
          confidence: 1.0,
          urgency: 'high',
          actionable: true,
          dismissible: false,
          icon: 'wrench'
        });
      }

    } catch (error) {
      console.error('Failed to fetch overdue items:', error);
    }

    return suggestions;
  }

  /**
   * Incomplete work (drafts, pending entries)
   */
  private async getIncompleteSuggestions(
    companyId: string,
    userId: string
  ): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];

    try {
      // Check for draft entries
      const draftsRef = collection(db, `companies/${companyId}/draft_entries`);
      const draftsQuery = query(
        draftsRef,
        where('userId', '==', userId),
        where('status', '==', 'draft'),
        limit(5)
      );

      const draftsSnapshot = await getDocs(draftsQuery);

      if (draftsSnapshot.size > 0) {
        suggestions.push({
          type: 'incomplete',
          text: `📝 ${draftsSnapshot.size} incomplete entries`,
          command: 'show my drafts',
          action: 'show_drafts',
          priority: 8,
          reason: 'Resume unfinished work',
          confidence: 0.95,
          urgency: 'medium',
          actionable: true,
          dismissible: true,
          icon: 'file-edit'
        });
      }

    } catch (error) {
      console.error('Failed to fetch incomplete items:', error);
    }

    return suggestions;
  }

  /**
   * ML-based predictive suggestions
   */
  private async getPredictiveSuggestions(
    context: AppContext,
    companyId: string
  ): Promise<ProactiveSuggestion[]> {
    const suggestions: ProactiveSuggestion[] = [];

    try {
      // Use tenant learning engine to predict likely actions
      const learningData = await tenantLearningEngine.getTenantData(companyId);
      
      // Analyze top patterns
      const topPatterns = learningData.stats.topPatterns.slice(0, 3);
      
      for (const patternKey of topPatterns) {
        const pattern = learningData.commandPatterns[patternKey];
        if (pattern && pattern.confidence > 0.8) {
          // Get most recent example
          const example = pattern.examples[0];
          
          suggestions.push({
            type: 'predictive',
            text: `💡 ${example}`,
            command: example,
            action: pattern.action,
            priority: 6,
            reason: 'You use this often',
            confidence: pattern.confidence,
            urgency: 'low',
            actionable: true,
            dismissible: true,
            icon: 'lightbulb'
          });
        }
      }

      // Context-based predictions
      if (context.selectedWell && context.currentScreen === 'well-detail') {
        // If user is viewing a well, likely to add entry
        suggestions.push({
          type: 'predictive',
          text: `📊 Add production entry for ${context.selectedWell.name}`,
          command: `add entry for well ${context.selectedWell.name}`,
          action: 'create_well_entry',
          priority: 8,
          reason: 'Common action when viewing well details',
          confidence: 0.85,
          urgency: 'medium',
          actionable: true,
          dismissible: true,
          icon: 'plus-circle'
        });
      }

    } catch (error) {
      console.error('Failed to generate predictive suggestions:', error);
    }

    return suggestions;
  }

  /**
   * Check if pattern matches current time
   */
  private patternMatchesTime(pattern: UserPattern, now: Date): boolean {
    const hour = now.getHours();
    const day = now.getDay();
    const date = now.getDate();

    if (pattern.type === 'daily') {
      if (pattern.timeOfDay !== undefined) {
        return Math.abs(hour - pattern.timeOfDay) <= 1; // Within 1 hour
      }
    }

    if (pattern.type === 'weekly') {
      if (pattern.dayOfWeek !== undefined && pattern.timeOfDay !== undefined) {
        return day === pattern.dayOfWeek && Math.abs(hour - pattern.timeOfDay) <= 1;
      }
    }

    if (pattern.type === 'monthly') {
      if (pattern.dayOfMonth !== undefined) {
        return date === pattern.dayOfMonth;
      }
    }

    return false;
  }

  /**
   * Dismiss a suggestion
   */
  dismissSuggestion(suggestion: ProactiveSuggestion): void {
    const key = this.getSuggestionKey(suggestion);
    this.dismissedSuggestions.add(key);

    // Auto-clear after 24 hours
    setTimeout(() => {
      this.dismissedSuggestions.delete(key);
    }, 24 * 60 * 60 * 1000);
  }

  /**
   * Get unique key for suggestion
   */
  private getSuggestionKey(suggestion: ProactiveSuggestion): string {
    return `${suggestion.type}:${suggestion.action}`;
  }

  /**
   * Learn from user actions
   */
  async learnPattern(
    userId: string,
    action: string,
    timestamp: Date = new Date()
  ): Promise<void> {
    const userPatterns = this.patterns.get(userId) || [];
    const hour = timestamp.getHours();
    const day = timestamp.getDay();

    // Find existing pattern or create new
    let pattern = userPatterns.find(p => 
      p.action === action && 
      p.type === 'daily' && 
      p.timeOfDay === hour
    );

    if (pattern) {
      // Update existing pattern
      pattern.frequency++;
      pattern.lastOccurrence = timestamp;
      pattern.confidence = Math.min(0.95, pattern.confidence + 0.05);
    } else {
      // Create new pattern
      pattern = {
        id: `${userId}_${action}_${Date.now()}`,
        companyId: getCompanyId(),
        userId,
        type: 'daily',
        timeOfDay: hour,
        action,
        frequency: 1,
        lastOccurrence: timestamp,
        confidence: 0.3
      };
      userPatterns.push(pattern);
    }

    this.patterns.set(userId, userPatterns);

    // Persist patterns (optional - can be saved to Firestore)
    // await this.savePatterns(userId);
  }

  /**
   * Load patterns from storage
   */
  private async loadPatterns(): Promise<void> {
    // TODO: Load from Firestore or IndexedDB
  }

  /**
   * Clear all dismissed suggestions
   */
  clearDismissed(): void {
    this.dismissedSuggestions.clear();
  }
}

// Export singleton
export const proactiveAssistant = ProactiveAssistant.getInstance();
